/**
 * Ideas Screen - MOBILE ONLY
 * 
 * Collaborative ideation for an opportunity.
 * Partners can share ideas, vote, and discuss.
 */

import React, { useState, useCallback } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { IdeaWithDetails, IdeaStatus, CreateIdeaRequest } from '../../../shared/types/idea';
import { useIdeas } from '../../../hooks/useIdeas';
import { ScreenContainer, ScreenHeader } from '../../../components/v2/mobile';
import { IdeasList, CreateIdeaModal } from '../../../components/v2/ideas';

export default function IdeasScreen() {
  const router = useRouter();
  const { opportunityId } = useLocalSearchParams<{ opportunityId: string }>();
  const oppId = opportunityId ? parseInt(opportunityId, 10) : 0;

  const {
    ideas,
    loading,
    creating,
    error,
    filters,
    setStatus,
    createIdea,
    refresh,
  } = useIdeas({ opportunityId: oppId });

  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleBack = useCallback(() => router.back(), [router]);

  const handleCreate = useCallback(async (data: CreateIdeaRequest): Promise<boolean> => {
    const idea = await createIdea(data);
    if (idea) {
      setShowCreateModal(false);
      return true;
    }
    return false;
  }, [createIdea]);

  const handleIdeaPress = useCallback((idea: IdeaWithDetails) => {
    router.push({
      pathname: '/v2/property/idea/[id]',
      params: { id: idea.id.toString() },
    });
  }, [router]);

  const handleFilterChange = useCallback((status: IdeaStatus | 'all') => {
    setStatus(status);
  }, [setStatus]);

  return (
    <ScreenContainer>
      <ScreenHeader title="Ideas" onBack={handleBack} />

      <IdeasList
        ideas={ideas}
        loading={loading}
        error={error}
        filter={filters.status || 'all'}
        onFilterChange={handleFilterChange}
        onIdeaPress={handleIdeaPress}
        onAddPress={() => setShowCreateModal(true)}
        onRefresh={refresh}
      />

      <CreateIdeaModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreate}
        creating={creating}
        apiError={error}
      />
    </ScreenContainer>
  );
}

